import React, { useState, useMemo } from 'react';
import './usertools-css/MineralCalciumCalculator.css';

// --- Standards & Clinical DRI Constants ---
// Official Dietary Reference Intakes (Institute of Medicine / National Academies / NIH ODS)
const CALCIUM_RDA_TABLE = {
  '0-6m': { rda: 200, isAI: true, ul: null, label: 'Infants 0-6 months' },
  '7-12m': { rda: 260, isAI: true, ul: null, label: 'Infants 7-12 months' },
  '1-3y': { rda: 700, isAI: false, ul: 2500, label: 'Children 1-3 years' },
  '4-8y': { rda: 1000, isAI: false, ul: 2500, label: 'Children 4-8 years' },
  '9-18y': { rda: 1300, isAI: false, ul: 3000, label: 'Teens 9-18 years (Peak Growth)' },
  '19-50y': { rda: 1000, isAI: false, ul: 2500, label: 'Adults 19-50 years' },
  'women_51-70y': { rda: 1200, isAI: false, ul: 2000, label: 'Post-Menopausal / Women 51-70 yrs' },
  'men_51-70y': { rda: 1000, isAI: false, ul: 2000, label: 'Men 51-70 years' },
  '71+y': { rda: 1200, isAI: false, ul: 2000, label: 'Adults 71+ years' },
  'pregnant_lactating_lte18y': { rda: 1300, isAI: false, ul: 3000, label: 'Pregnant/Lactating ≤18 years' },
  'pregnant_lactating_19plus': { rda: 1000, isAI: false, ul: 2500, label: 'Pregnant/Lactating 19+ years' },
};

// Nutritional values per typical serving (mg elemental Ca)
const FOOD_CA_VALUES = {
  dairy_milk_cup: { mg: 300, label: 'Milk (Cow or Fortified Plant, 1 cup)' },
  yogurt_plain_cup: { mg: 300, label: 'Yogurt (Plain / Greek, ¾ cup)' },
  cheese_cheddar_45g: { mg: 300, label: 'Hard Cheese (Cheddar/Swiss, 1.5 oz / 45g)' },
  tofu_ca_set_85g: { mg: 250, label: 'Calcium-Set Tofu (3 oz / 85g)' },
  sardines_w_bones_85g: { mg: 325, label: 'Canned Sardines with bones (3 oz / 85g)' },
  kale_cooked_cup: { mg: 150, label: 'Low-Oxalate Greens (Cooked Kale/Bok Choy, 1 cup)' },
  spinach_cooked_half_cup: { mg: 120, label: 'Cooked Spinach (½ cup · High Oxalate / ~5% abs)' },
};

const SUPPLEMENT_CATALOG = [
  { id: 'carbonate_500', name: 'Calcium Carbonate (500 mg elemental)', form: 'carbonate', elementalMg: 500 },
  { id: 'citrate_300', name: 'Calcium Citrate (300 mg elemental)', form: 'citrate', elementalMg: 300 },
  { id: 'chewable_250', name: 'Chewable / Mixed Calcium (250 mg elemental)', form: 'mixed', elementalMg: 250 },
  { id: 'combo_600_d', name: 'Calcium Carbonate (600 mg) + Vitamin D', form: 'carbonate', elementalMg: 600 },
  { id: 'other', name: 'Other / Custom Dose', form: 'other', elementalMg: 0 },
];

function getCalciumIntakeKey(ageNum, sex, isMenopausal, isPregnant, isLactating) {
  if (isPregnant || isLactating) {
    return ageNum <= 18 ? 'pregnant_lactating_lte18y' : 'pregnant_lactating_19plus';
  }
  if (ageNum < 0.5) return '0-6m';
  if (ageNum < 1) return '7-12m';
  if (ageNum <= 3) return '1-3y';
  if (ageNum <= 8) return '4-8y';
  if (ageNum <= 18) return '9-18y';
  if (ageNum <= 50) return '19-50y';
  if (ageNum <= 70) {
    if (sex === 'female' || isMenopausal) return 'women_51-70y';
    return 'men_51-70y';
  }
  return '71+y';
}

function MineralCalciumCalculator() {
  // Demographics starting from scratch (empty)
  const [age, setAge] = useState('');
  const [sex, setSex] = useState('');
  const [isMenopausal, setIsMenopausal] = useState(false);
  const [isPregnant, setIsPregnant] = useState(false);
  const [isLactating, setIsLactating] = useState(false);

  // Bone-density risk flags
  const [boneRisks, setBoneRisks] = useState({
    familyHipFracture: false,
    smallFrame: false,
    smoking: false,
    steroidUse: false,
  });

  // Food servings (all start empty from scratch)
  const [dietServings, setDietServings] = useState({
    dairy_milk_cup: '',
    yogurt_plain_cup: '',
    cheese_cheddar_45g: '',
    tofu_ca_set_85g: '',
    sardines_w_bones_85g: '',
    kale_cooked_cup: '',
    spinach_cooked_half_cup: '',
  });

  // Absorption and excretion modifiers
  const [vitaminDStatus, setVitaminDStatus] = useState('adequate'); // 'adequate' | 'low'
  const [highSodium, setHighSodium] = useState(false);
  const [highCaffeine, setHighCaffeine] = useState(false);
  const [highOxalateMeals, setHighOxalateMeals] = useState(false);

  // Supplements
  const [takesSupplement, setTakesSupplement] = useState(false);
  const [selectedSupplementId, setSelectedSupplementId] = useState('');
  const [customSupplementMg, setCustomSupplementMg] = useState('');
  const [supplementDosesPerDay, setSupplementDosesPerDay] = useState('');
  const [supplementTiming, setSupplementTiming] = useState('with_food');

  // Clinical Conditions
  const [medsConditions, setMedsConditions] = useState({
    hasPPIs: false,
    hasCKD: false,
    hasKidneyStones: false,
    hasHyperparathyroidism: false,
  });

  // Optional
  const [dexaTScore, setDexaTScore] = useState('');

  // Handlers
  const handleBoneRiskChange = (e) => {
    const { name, checked } = e.target;
    setBoneRisks(prev => ({ ...prev, [name]: checked }));
  };

  const handleDietChange = (e) => {
    const { name, value } = e.target;
    setDietServings(prev => ({ ...prev, [name]: value }));
  };

  const handleMedsChange = (e) => {
    const { name, checked } = e.target;
    setMedsConditions(prev => ({ ...prev, [name]: checked }));
  };

  // --- Dynamic Live Calculation ---
  const results = useMemo(() => {
    const hasAge = parseFloat(age) > 0;
    const hasSex = Boolean(sex);

    // Initial awaiting state when core demographics are not entered yet
    if (!hasAge && !hasSex) {
      return {
        isAwaiting: true,
        error: 'Enter your age and biological sex in the form on the left to calculate your personalized calcium balance.',
      };
    }
    if (!hasAge) {
      return {
        isAwaiting: false,
        error: 'Please enter a valid age.',
      };
    }
    if (!hasSex) {
      return {
        isAwaiting: false,
        error: 'Please select your biological sex to determine clinical dietary reference intakes (DRIs).',
      };
    }

    const ageNum = parseFloat(age);

    // 1. Target RDA / AI & UL
    const intakeKey = getCalciumIntakeKey(ageNum, sex, isMenopausal, isPregnant, isLactating);
    const rdaEntry = CALCIUM_RDA_TABLE[intakeKey] || CALCIUM_RDA_TABLE['19-50y'];
    let baseRda = rdaEntry.rda;
    const currentUL = rdaEntry.ul;

    // Bone Health Risk Adjustments (+10% physiological buffer)
    const hasBoneRisk = Object.values(boneRisks).some(Boolean);
    let targetRda = baseRda;
    if (hasBoneRisk) {
      targetRda = Math.round(baseRda * 1.10);
    }

    // 2. Dietary Calcium (Raw mg)
    let rawDietExSpinach = 0;
    let rawSpinachCa = 0;

    for (const [key, servingStr] of Object.entries(dietServings)) {
      const servings = parseFloat(servingStr) || 0;
      if (servings > 0 && FOOD_CA_VALUES[key]) {
        const mg = servings * FOOD_CA_VALUES[key].mg;
        if (key === 'spinach_cooked_half_cup') {
          rawSpinachCa += mg;
        } else {
          rawDietExSpinach += mg;
        }
      }
    }
    const rawDietTotal = Math.round(rawDietExSpinach + rawSpinachCa);

    // 3. Supplemental Calcium
    let suppTotal = 0;
    let singleDoseMg = 0;
    let suppForm = 'none';

    if (takesSupplement) {
      const dosesPerDay = parseFloat(supplementDosesPerDay) || 0;
      if (selectedSupplementId === 'other') {
        singleDoseMg = parseFloat(customSupplementMg) || 0;
        suppForm = 'other';
      } else {
        const item = SUPPLEMENT_CATALOG.find(s => s.id === selectedSupplementId);
        singleDoseMg = item ? item.elementalMg : 0;
        suppForm = item ? item.form : 'other';
      }
      suppTotal = Math.round(singleDoseMg * dosesPerDay);
    }

    // 4. Total Daily Intake & Balance
    const totalIntake = rawDietTotal + suppTotal;
    const gap = targetRda - totalIntake; // positive = deficit, negative = surplus

    // 5. Contextual Absorbed & Urinary Clearance Estimates
    let dietAbsFactor = 0.30; // ~30% base absorption
    if (vitaminDStatus === 'low') dietAbsFactor -= 0.10;
    if (highOxalateMeals) dietAbsFactor -= 0.08;
    if (medsConditions.hasPPIs) dietAbsFactor *= 0.90;
    dietAbsFactor = Math.max(0.12, dietAbsFactor);

    // Spinach fractional absorption is ~5% due to high oxalate complexing
    const absorbedDietExSpinach = rawDietExSpinach * dietAbsFactor;
    const absorbedSpinach = rawSpinachCa * 0.05;
    const absorbedDietTotal = absorbedDietExSpinach + absorbedSpinach;

    // Supplement absorption by form & meal timing
    let suppAbsFactor = 0.25;
    if (suppForm === 'citrate') {
      suppAbsFactor = 0.30; // acid-independent
    } else if (suppForm === 'carbonate') {
      suppAbsFactor = supplementTiming === 'with_food' ? 0.26 : 0.18;
      if (medsConditions.hasPPIs) suppAbsFactor *= 0.85;
    }
    const absorbedSuppTotal = suppTotal * suppAbsFactor;

    // Urinary excretion losses
    let urinaryLoss = 0;
    if (highSodium) urinaryLoss += 40;
    if (highCaffeine) urinaryLoss += 40;

    const netAbsorbedEstimate = Math.max(0, Math.round(absorbedDietTotal + absorbedSuppTotal - urinaryLoss));

    // 6. Dynamic Badge Status (Green, Orange, Red)
    let badgeClass = 'ca-badge-green';
    let badgeLabel = 'Adequate Intake';

    if (medsConditions.hasCKD || medsConditions.hasHyperparathyroidism) {
      badgeClass = 'ca-badge-red';
      badgeLabel = 'Renal / Endocrine Supervision Required';
    } else if (currentUL !== null && totalIntake > currentUL) {
      badgeClass = 'ca-badge-red';
      badgeLabel = `Exceeds Upper Limit (${totalIntake} mg)`;
    } else if (totalIntake < targetRda * 0.50) {
      badgeClass = 'ca-badge-red';
      badgeLabel = `Severe Deficit (-${gap} mg/day)`;
    } else if (gap > 150) {
      badgeClass = 'ca-badge-orange';
      badgeLabel = `Dietary Deficit (-${gap} mg/day)`;
    } else if (gap > 30) {
      badgeClass = 'ca-badge-orange';
      badgeLabel = `Borderline Intake (-${gap} mg/day)`;
    } else {
      badgeClass = 'ca-badge-green';
      badgeLabel = gap <= 0 ? 'Optimal · Target Met' : 'Adequate Intake';
    }

    return {
      isAwaiting: false,
      baseRda,
      targetRda,
      isAI: rdaEntry.isAI,
      currentUL,
      rawDietTotal,
      rawSpinachCa,
      suppTotal,
      singleDoseMg,
      suppForm,
      totalIntake,
      gap,
      dietAbsFactor,
      absorbedDietTotal: Math.round(absorbedDietTotal),
      absorbedSuppTotal: Math.round(absorbedSuppTotal),
      urinaryLoss,
      netAbsorbedEstimate,
      badgeClass,
      badgeLabel,
      error: null,
    };
  }, [
    age,
    sex,
    isMenopausal,
    isPregnant,
    isLactating,
    boneRisks,
    dietServings,
    vitaminDStatus,
    highSodium,
    highCaffeine,
    highOxalateMeals,
    takesSupplement,
    selectedSupplementId,
    customSupplementMg,
    supplementDosesPerDay,
    supplementTiming,
    medsConditions,
  ]);

  return (
    <div className="mineralCalciumCalculator">
      {/* Hero Header */}
      <div className="ca-hero">
        <div>
          <p className="ca-eyebrow">Pure Health Nexus / Bone &amp; Mineral Tools</p>
          <h1>Fortify your bones.<br />Optimize your calcium.</h1>
          <p className="ca-hero-desc">
            Calculate your clinical calcium requirement based on Institute of Medicine (IOM) standards, dietary bioavailability, and bone health risks.
          </p>

          {/* AI Promo Callout Banner (Completely Isolated) */}
          <div className="ca-promo-banner">
            <strong>Unlock Smarter Eating:</strong> Get a complete breakdown of vitamins, minerals, and fats plus a personalized weight-loss plan or nutrition tailored to your training with a single{' '}
            <a href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals" className="ca-promo-link">
              AI scan
            </a>
          </div>
        </div>

        <div className="ca-hero-art" aria-hidden="true">
          <img
            src="/assets/images/appscalcium.webp"
            alt="Calcium Calculator Icon"
            className="ca-art-icon"
            loading="lazy"
            style={{ width: '120px', height: '120px', objectFit: 'contain' }}
          />
          <span className="ca-art-caption">BONE MATRIX PRECISION</span>
        </div>
      </div>

      <div className="ca-layout">
        {/* Left Column: Form Panel */}
        <div className="ca-panel ca-input-panel">
          <div className="ca-panel-heading">
            <div>
              <p className="ca-eyebrow">01 / Profile</p>
              <h2>Your Details</h2>
              <p>Enter your daily food habits and clinical risk profile.</p>
            </div>
            <span className="ca-step">01</span>
          </div>

          {/* Demographics */}
          <div className="ca-field-row">
            <label className="ca-field">
              <span className="ca-field-label">Age</span>
              <input
                type="number"
                min="1"
                max="115"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Years (e.g. 35)"
              />
            </label>
            <label className="ca-field">
              <span className="ca-field-label">Sex at Birth</span>
              <select value={sex} onChange={(e) => setSex(e.target.value)}>
                <option value="">-- Select Sex --</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </label>
          </div>

          {sex === 'female' && parseFloat(age) >= 40 && parseFloat(age) <= 70 && (
            <label className="ca-checkbox-label">
              <input
                type="checkbox"
                checked={isMenopausal}
                onChange={(e) => setIsMenopausal(e.target.checked)}
              />
              Post-Menopausal (Elevates RDA to 1,200 mg/day)
            </label>
          )}

          {sex === 'female' && parseFloat(age) >= 14 && parseFloat(age) <= 50 && (
            <div className="ca-field-row">
              <label className="ca-checkbox-label">
                <input
                  type="checkbox"
                  checked={isPregnant}
                  onChange={(e) => {
                    setIsPregnant(e.target.checked);
                    if (e.target.checked) setIsLactating(false);
                  }}
                />
                Currently Pregnant
              </label>
              <label className="ca-checkbox-label">
                <input
                  type="checkbox"
                  checked={isLactating}
                  onChange={(e) => {
                    setIsLactating(e.target.checked);
                    if (e.target.checked) setIsPregnant(false);
                  }}
                />
                Currently Lactating
              </label>
            </div>
          )}

          {/* Bone Health Risks */}
          <div className="ca-section-label">
            <span>Bone Health Risks</span>
            <small style={{ fontWeight: 400, color: 'var(--ca-muted)', textTransform: 'none' }}>Target buffer</small>
          </div>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="familyHipFracture"
              checked={boneRisks.familyHipFracture}
              onChange={handleBoneRiskChange}
            />
            Family history of hip fracture / osteoporosis
          </label>
          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="smallFrame"
              checked={boneRisks.smallFrame}
              onChange={handleBoneRiskChange}
            />
            Slender body frame / low BMI (&lt; 19)
          </label>
          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="smoking"
              checked={boneRisks.smoking}
              onChange={handleBoneRiskChange}
            />
            Current tobacco smoker
          </label>
          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="steroidUse"
              checked={boneRisks.steroidUse}
              onChange={handleBoneRiskChange}
            />
            Long-term corticosteroid medication (Prednisone)
          </label>

          {/* Dietary Servings */}
          <div className="ca-section-label">
            <span>Daily Calcium Food Servings</span>
            <small style={{ fontWeight: 400, color: 'var(--ca-muted)', textTransform: 'none' }}>Avg / day</small>
          </div>

          <div className="ca-diet-grid">
            {Object.entries(FOOD_CA_VALUES).map(([key, item]) => (
              <div key={key} className="ca-diet-item">
                <span>{item.label}</span>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.25"
                  name={key}
                  value={dietServings[key] || ''}
                  onChange={handleDietChange}
                  placeholder="0"
                />
                <small>~{item.mg} mg per serving</small>
              </div>
            ))}
          </div>

          {/* Bioavailability & Losses */}
          <div className="ca-section-label">
            <span>Absorption Modifiers</span>
            <small style={{ fontWeight: 400, color: 'var(--ca-muted)', textTransform: 'none' }}>Gut uptake</small>
          </div>

          <label className="ca-field">
            <span className="ca-field-label">Vitamin D Status</span>
            <select value={vitaminDStatus} onChange={(e) => setVitaminDStatus(e.target.value)}>
              <option value="adequate">Adequate / Tested Sufficient (25-OH-D ≥ 30 ng/mL)</option>
              <option value="low">Low / Insufficient (Impairs active gut transport)</option>
            </select>
          </label>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              checked={highSodium}
              onChange={(e) => setHighSodium(e.target.checked)}
            />
            High Sodium Intake (&gt; 2,300 mg/day · increases urinary Ca leak)
          </label>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              checked={highCaffeine}
              onChange={(e) => setHighCaffeine(e.target.checked)}
            />
            High Caffeine Intake (&gt; 3 cups coffee / 300+ mg/day)
          </label>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              checked={highOxalateMeals}
              onChange={(e) => setHighOxalateMeals(e.target.checked)}
            />
            Regular high-oxalate foods with calcium meals (rhubarb, beet greens)
          </label>

          {/* Supplements */}
          <div className="ca-section-label">
            <span>Calcium Supplements</span>
            <small style={{ fontWeight: 400, color: 'var(--ca-muted)', textTransform: 'none' }}>Max 500 mg bolus</small>
          </div>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              checked={takesSupplement}
              onChange={(e) => setTakesSupplement(e.target.checked)}
            />
            I currently take a calcium supplement
          </label>

          {takesSupplement && (
            <div style={{ animation: 'fadeIn 0.2s ease' }}>
              <label className="ca-field">
                <span className="ca-field-label">Supplement Type</span>
                <select
                  value={selectedSupplementId}
                  onChange={(e) => setSelectedSupplementId(e.target.value)}
                >
                  <option value="">-- Select Supplement --</option>
                  {SUPPLEMENT_CATALOG.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>

              {selectedSupplementId === 'other' && (
                <label className="ca-field">
                  <span className="ca-field-label">Elemental Calcium per Dose (mg)</span>
                  <input
                    type="number"
                    min="1"
                    max="1500"
                    value={customSupplementMg}
                    onChange={(e) => setCustomSupplementMg(e.target.value)}
                    placeholder="e.g. 500"
                  />
                </label>
              )}

              <div className="ca-field-row">
                <label className="ca-field">
                  <span className="ca-field-label">Doses Per Day</span>
                  <input
                    type="number"
                    min="1"
                    max="6"
                    value={supplementDosesPerDay}
                    onChange={(e) => setSupplementDosesPerDay(e.target.value)}
                    placeholder="e.g. 1"
                  />
                </label>
                <label className="ca-field">
                  <span className="ca-field-label">Meal Timing</span>
                  <select
                    value={supplementTiming}
                    onChange={(e) => setSupplementTiming(e.target.value)}
                  >
                    <option value="with_food">With Meals</option>
                    <option value="without_food">Empty Stomach</option>
                  </select>
                </label>
              </div>
              <p className="ca-hint">
                Calcium carbonate requires stomach acid (take with food). Citrate is acid-independent.
              </p>
            </div>
          )}

          {/* Clinical Conditions */}
          <div className="ca-section-label">
            <span>Clinical Conditions &amp; Meds</span>
            <small style={{ fontWeight: 400, color: 'var(--ca-muted)', textTransform: 'none' }}>Precautions</small>
          </div>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="hasPPIs"
              checked={medsConditions.hasPPIs}
              onChange={handleMedsChange}
            />
            Regular PPI use (Omeprazole, Pantoprazole) · Reduces carbonate absorption
          </label>

          <label className="ca-checkbox-label">
            <input
              type="checkbox"
              name="hasKidneyStones"
              checked={medsConditions.hasKidneyStones}
              onChange={handleMedsChange}
            />
            History of recurrent calcium-oxalate Kidney Stones
          </label>

          <label className="ca-checkbox-label" style={{ color: '#b91c1c', fontWeight: 600 }}>
            <input
              type="checkbox"
              name="hasCKD"
              checked={medsConditions.hasCKD}
              onChange={handleMedsChange}
            />
            Chronic Kidney Disease (CKD) · Requires physician oversight
          </label>

          <label className="ca-checkbox-label" style={{ color: '#b91c1c', fontWeight: 600 }}>
            <input
              type="checkbox"
              name="hasHyperparathyroidism"
              checked={medsConditions.hasHyperparathyroidism}
              onChange={handleMedsChange}
            />
            Hyperparathyroidism / High Blood Calcium
          </label>

          {/* Optional DEXA */}
          <label className="ca-field" style={{ marginTop: '12px' }}>
            <span className="ca-field-label">DEXA Bone Density T-Score (Optional)</span>
            <input
              type="number"
              step="0.1"
              value={dexaTScore}
              onChange={(e) => setDexaTScore(e.target.value)}
              placeholder="e.g. -1.8 (Osteopenia) or -2.6 (Osteoporosis)"
            />
          </label>
        </div>

        {/* Right Column: Results & Analytics */}
        <div className="ca-results">
          {results.error ? (
            <div className="ca-panel ca-result-hero">
              <div className="ca-result-top">
                <div>
                  <p className="ca-eyebrow">02 / Assessment</p>
                  <h2>Daily Calcium Balance</h2>
                </div>
                <span className={`ca-badge ${results.isAwaiting ? 'ca-badge-orange' : 'ca-badge-red'}`}>
                  {results.isAwaiting ? 'Awaiting Input' : 'Incomplete Input'}
                </span>
              </div>
              <div style={{ padding: '16px 0 6px' }}>
                <p style={{ fontSize: '1rem', color: 'var(--ca-text)', margin: '0 0 8px' }}>
                  {results.error}
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--ca-muted)', margin: 0 }}>
                  Enter your details and daily food servings in the panel on the left to evaluate your bone health target, gut absorption factors, and safety limits.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Primary Assessment Hero Card with Dynamic 3-Color Badge */}
              <section className="ca-panel ca-result-hero">
                <div className="ca-result-top">
                  <div>
                    <p className="ca-eyebrow">02 / Assessment</p>
                    <h2>Daily Calcium Balance</h2>
                  </div>

                  {/* Dynamic Badge auto-adjusting to Green, Orange, or Red */}
                  <span className={`ca-badge ${results.badgeClass}`}>
                    <span style={{ fontSize: '0.8em' }}>●</span> {results.badgeLabel}
                  </span>
                </div>

                <div className="ca-metrics-grid">
                  <div className="ca-metric-cell">
                    <span className="ca-metric-label">Personalized Target</span>
                    <div className="ca-metric-val">
                      {results.targetRda}
                      <span> mg/day</span>
                    </div>
                    <div className="ca-metric-sub">
                      Base IOM {results.isAI ? 'AI' : 'RDA'}: {results.baseRda} mg/day
                    </div>
                  </div>

                  <div className="ca-metric-cell">
                    <span className="ca-metric-label">Total Daily Intake</span>
                    <div className="ca-metric-val">
                      {results.totalIntake}
                      <span> mg/day</span>
                    </div>
                    <div className="ca-metric-sub">
                      {results.gap > 0 ? (
                        <span style={{ color: 'var(--ca-orange)' }}>
                          Deficit: ~{results.gap} mg/day below target
                        </span>
                      ) : (
                        <span style={{ color: 'var(--ca-green)' }}>
                          Surplus: +{Math.abs(results.gap)} mg/day above target
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Visual Progress Bar */}
                <div className="ca-progress-wrap">
                  <div className="ca-progress-track">
                    <div
                      className="ca-progress-fill-diet"
                      style={{
                        width: `${Math.min(100, (results.rawDietTotal / (results.targetRda || 1000)) * 100)}%`,
                      }}
                      title={`Dietary Calcium: ${results.rawDietTotal} mg`}
                    />
                    <div
                      className="ca-progress-fill-supp"
                      style={{
                        width: `${Math.min(
                          Math.max(0, 100 - (results.rawDietTotal / (results.targetRda || 1000)) * 100),
                          (results.suppTotal / (results.targetRda || 1000)) * 100
                        )}%`,
                      }}
                      title={`Supplemental Calcium: ${results.suppTotal} mg`}
                    />
                  </div>
                  <div className="ca-progress-legend">
                    <span>
                      <span className="ca-dot ca-dot-diet" /> Dietary Calcium: <b>{results.rawDietTotal} mg</b>
                    </span>
                    <span>
                      <span className="ca-dot ca-dot-supp" /> Supplemental: <b>{results.suppTotal} mg</b>
                    </span>
                  </div>
                </div>

                {/* Quick Stats Row */}
                <div className="ca-stats">
                  <div>
                    <small>Dietary (Food)</small>
                    <strong>{results.rawDietTotal} mg</strong>
                  </div>
                  <div>
                    <small>Supplemental</small>
                    <strong>{results.suppTotal} mg</strong>
                  </div>
                  <div>
                    <small>Tolerable Upper Limit (UL)</small>
                    <strong>{results.currentUL ? `${results.currentUL} mg` : 'ND'}</strong>
                  </div>
                </div>
              </section>

              {/* Safety Warnings & Alerts */}
              {(medsConditions.hasCKD || medsConditions.hasHyperparathyroidism) && (
                <div className="ca-error-notice">
                  <strong>CRITICAL CLINICAL ALERT:</strong> You selected Chronic Kidney Disease or Hyperparathyroidism. In these conditions, calcium and phosphate homeostasis is impaired. Calcium supplementation must be managed directly by a nephrologist or endocrinologist to prevent soft tissue and arterial calcification.
                </div>
              )}

              {results.currentUL !== null && results.totalIntake > results.currentUL && (
                <div className="ca-error-notice">
                  <strong>TOLERABLE UPPER LIMIT (UL) EXCEEDED:</strong> Your total calcium intake (~{results.totalIntake} mg/day) exceeds the safe upper limit of <strong>{results.currentUL} mg/day</strong>. Long-term excess calcium intake increases risks of kidney stones, hypercalcemia, and vascular calcification.
                </div>
              )}

              {takesSupplement && results.singleDoseMg > 500 && (
                <div className="ca-warning-notice">
                  <strong>ABSORPTION CEILING WARNING:</strong> Your reported supplement dose is {results.singleDoseMg} mg elemental calcium in a single serving. Intestinal calcium transport saturation drops sharply for single doses &gt; 500 mg. For optimal absorption, split doses into 250-500 mg servings taken hours apart.
                </div>
              )}

              {medsConditions.hasKidneyStones && (
                <div className="ca-warning-notice">
                  <strong>KIDNEY STONE NOTE:</strong> Contrary to common misconception, meeting your calcium RDA through <em>dietary foods with meals</em> binds oxalate in the intestines and lowers kidney stone recurrence. Avoid taking high-dose calcium supplements on an empty stomach.
                </div>
              )}

              {/* Analytical Breakdown Table (Isolated ca-table) */}
              <section className="ca-panel ca-module">
                <div className="ca-module-heading">
                  <p className="ca-eyebrow">03 / Analytics</p>
                  <h2>Intake &amp; Bioavailability Breakdown</h2>
                  <p>Accounting for food matrix effects, stomach acid, and urinary losses.</p>
                </div>

                <div className="ca-table-wrap">
                  <table className="ca-table">
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Value</th>
                        <th>Status / Factor</th>
                        <th>Physiological Mechanism</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Base Dietary Requirement</strong></td>
                        <td>{results.baseRda} mg/day</td>
                        <td><span className="ca-badge ca-badge-green">IOM Standard</span></td>
                        <td>Institute of Medicine age- and sex-based RDA or AI.</td>
                      </tr>
                      <tr>
                        <td><strong>Personalized Target</strong></td>
                        <td>{results.targetRda} mg/day</td>
                        <td>
                          {results.targetRda > results.baseRda ? (
                            <span className="ca-badge ca-badge-orange">+10% Bone Risk Buffer</span>
                          ) : (
                            <span className="ca-badge ca-badge-green">Standard</span>
                          )}
                        </td>
                        <td>Adjusted for osteoporosis family history, frame size, or steroid use.</td>
                      </tr>
                      <tr>
                        <td><strong>Dietary Calcium (Total)</strong></td>
                        <td>{results.rawDietTotal} mg/day</td>
                        <td>Food Matrix</td>
                        <td>Total calcium ingested across milk, yogurt, cheese, and greens.</td>
                      </tr>
                      <tr>
                        <td><strong>Net Absorbed Estimate</strong></td>
                        <td>~{results.netAbsorbedEstimate} mg/day</td>
                        <td>Contextual View</td>
                        <td>Estimated elemental calcium absorbed into bloodstream after gut &amp; urine losses.</td>
                      </tr>
                      <tr>
                        <td><strong>Urinary Excretion Offset</strong></td>
                        <td>{results.urinaryLoss} mg/day</td>
                        <td>{results.urinaryLoss > 0 ? <span className="ca-badge ca-badge-orange">Elevated</span> : 'Normal'}</td>
                        <td>Excess sodium (&gt;2,300 mg) and caffeine increase urinary calcium wasting.</td>
                      </tr>
                      <tr>
                        <td><strong>Supplemental Calcium</strong></td>
                        <td>{results.suppTotal} mg/day</td>
                        <td>
                          {results.suppTotal > 0 ? (
                            <span className="ca-badge ca-badge-green">{results.suppForm}</span>
                          ) : (
                            'None'
                          )}
                        </td>
                        <td>Elemental calcium from pills, chews, or powders.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Actionable Nutrition Strategies */}
              <section className="ca-panel ca-module">
                <div className="ca-module-heading">
                  <p className="ca-eyebrow">04 / Strategy</p>
                  <h2>Evidence-Based Recommendations</h2>
                </div>

                <div className="ca-cards-grid">
                  <div className="ca-card">
                    <h4>Top Bioavailable Calcium Foods</h4>
                    <p className="ca-card-desc">
                      Prioritize foods with high fractional absorption:
                    </p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--ca-text)' }}>
                      <li><strong>Dairy Milk or Fortified Plant Milk:</strong> 1 cup ≈ 300 mg (32% absorbed)</li>
                      <li><strong>Cooked Bok Choy / Kale:</strong> 1 cup ≈ 150 mg (50% absorbed - low oxalate!)</li>
                      <li><strong>Calcium-Set Tofu:</strong> 3 oz ≈ 250 mg (31% absorbed)</li>
                      <li><strong>Plain Greek Yogurt:</strong> ¾ cup ≈ 300 mg</li>
                      <li><strong>Canned Sardines (with bones):</strong> 3 oz ≈ 325 mg</li>
                    </ul>
                  </div>

                  <div className="ca-card">
                    <h4>Supplement Selection &amp; Timing</h4>
                    <p className="ca-card-desc">
                      Maximize bone mineral deposition:
                    </p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--ca-text)' }}>
                      <li><strong>Calcium Citrate:</strong> Best for elderly, low stomach acid, or PPI users; can be taken with or without food.</li>
                      <li><strong>Calcium Carbonate:</strong> Inexpensive and compact; must be taken with meals for stomach acid dissolution.</li>
                      <li><strong>Rule of 500:</strong> Never take more than 500 mg elemental calcium in a single dose.</li>
                      <li><strong>Synergy:</strong> Ensure adequate Vitamin D and Vitamin K2 for bone mineralization.</li>
                    </ul>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Complete Educational Reference Guide */}
      <div id="calcium-education" className="ca-guide-section">
        <div className="ca-module-heading">
          <p className="ca-eyebrow">Educational Reference</p>
          <h2>Understanding Calcium Biochemistry, Bone Mass &amp; Health</h2>
          <p>Scientific mechanisms, absorption pathways, and clinical reference standards.</p>
        </div>

        {/* 1. Essential Roles Grid */}
        <div className="ca-def-grid">
          <div className="ca-def-card">
            <h4>1. Hydroxyapatite Bone Matrix (99% of Body Calcium)</h4>
            <p>
              Over 99% of body calcium is sequestered in bones and teeth in crystal form as <strong>calcium hydroxyapatite</strong>. Bone undergoes continuous remodeling through osteoclast resorption and osteoblast bone formation.
            </p>
          </div>

          <div className="ca-def-card">
            <h4>2. Ionized Calcium: Muscle &amp; Cardiac Signaling</h4>
            <p>
              The remaining 1% of calcium exists in blood and extracellular fluid. Tight serum regulation (8.5-10.2 mg/dL) is critical for cardiac myocyte contraction, neurotransmitter release at synapses, and the clotting cascade.
            </p>
          </div>

          <div className="ca-def-card">
            <h4>3. The Oxalate Paradox (Spinach vs. Kale)</h4>
            <p>
              Not all food calcium is bioavailable. While cooked spinach contains high calcium (120 mg/half cup), its high oxalic acid binds calcium into insoluble calcium oxalate, dropping absorption to ~5%. In contrast, low-oxalate kale and bok choy yield ~50% absorption.
            </p>
          </div>

          <div className="ca-def-card">
            <h4>4. Vitamin D &amp; Active Enterocyte Transport</h4>
            <p>
              Active intestinal calcium absorption relies on calcitriol (1,25-dihydroxyvitamin D), which upregulates enterocyte TRPV6 calcium channels and calbindin-D9k transport proteins. Low Vitamin D directly reduces calcium absorption by up to 50%.
            </p>
          </div>
        </div>

        {/* 2. Reference Categories Table */}
        <div className="ca-ref-grid">
          <div className="ca-ref-block">
            <h4>Recommended Dietary Allowances (RDA)</h4>
            <ul className="ca-ref-list">
              <li><span>Teens (9-18 yrs · Peak Bone Mass)</span> <strong>1,300 mg/day</strong></li>
              <li><span>Adult Men &amp; Women (19-50 yrs)</span> <strong>1,000 mg/day</strong></li>
              <li><span>Post-Menopausal Women (51-70 yrs)</span> <strong>1,200 mg/day</strong></li>
              <li><span>Men (51-70 yrs)</span> <strong>1,000 mg/day</strong></li>
              <li><span>Adults 71+ yrs</span> <strong>1,200 mg/day</strong></li>
              <li><span>Pregnancy / Lactation (19+ yrs)</span> <strong>1,000 mg/day</strong></li>
            </ul>
          </div>

          <div className="ca-ref-block">
            <h4>Tolerable Upper Intake Levels (UL)</h4>
            <ul className="ca-ref-list">
              <li><span>Infants 0-12 months</span> <strong>Not Determinable</strong></li>
              <li><span>Children 1-8 years</span> <strong>2,500 mg/day</strong></li>
              <li><span>Teens 9-18 years</span> <strong>3,000 mg/day</strong></li>
              <li><span>Adults 19-50 years</span> <strong>2,500 mg/day</strong></li>
              <li><span>Adults 51+ years</span> <strong>2,000 mg/day</strong></li>
              <li><span>Clinical Caution</span> <strong>Applies to Total Intake (Food + Supplements)</strong></li>
            </ul>
          </div>
        </div>

        {/* Clinical Sources */}
        <div className="ca-sources">
          <strong>Scientific Methodologies &amp; Clinical Sources:</strong>
          <br />
          • Institute of Medicine (US) Committee to Review Dietary Reference Intakes for Vitamin D and Calcium. (2011). <em>Dietary Reference Intakes for Calcium and Vitamin D</em>. Washington (DC): National Academies Press.
          <br />
          • National Institutes of Health, Office of Dietary Supplements. <em>Calcium Fact Sheet for Health Professionals</em>.
          <br />
          • Weaver, C. M., &amp; Plawecki, K. L. (1994). Dietary calcium: adequacy of a vegetarian diet. <em>American Journal of Clinical Nutrition</em>, 59(5 Suppl), 1238S-1241S.
          <br />
          • Heaney, R. P., et al. (1999). Absorbability of calcium sources: the limited role of solubility. <em>Calcified Tissue International</em>, 65(5), 334-337.
          <br />
          • Curhan, G. C., et al. (1993). A prospective study of dietary calcium and other nutrients and the risk of symptomatic kidney stones. <em>New England Journal of Medicine</em>, 328(12), 833-838.
        </div>
      </div>
    </div>
  );
}

export default MineralCalciumCalculator;